import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useFormik } from "formik";
import {
  Box,
  Button,
  Card,
  CardHeader,
  Divider,
  Grid,
  Link,
  TableCell,
  TableRow,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import queryString from "query-string";
import { formatInTimeZone } from "date-fns-tz";

import { SCREEN_PATHS } from "@APP/navigation";
import {
  AppState,
  clearOrgSearchResults,
  clearSearchResults,
  hideLoader,
  setOrganisationSearchResults,
  setSelectedUser,
  setUserSearchResults,
  showLoader,
} from "@APP/redux";
import { CommonTextField, Page, ScreenHeader, Table } from "@APP/components";
import { useAlert, useHandleErrorCodes } from "@APP/hooks";
import { API } from "@APP/services";
import { tableStyles as useStyles } from "@APP/styles/table";
import { formatErrorMessage } from "@APP/utils";
import {
  OrganisationSearchTerm,
  OrganisationSearchedData,
  UserSearchResponse,
  UserSearchTerms,
} from "@APP/types";
import { commonFormatDate } from "@APP/constants";

import OrganisationList from "../organisationDasboard/OrganisationList";

const UpdateUserView = () => {
  const alert = useAlert();
  const handleErrorCodes = useHandleErrorCodes();
  const dispatch = useDispatch();
  const history = useHistory();
  const classes = useStyles();
  const [page, setPage] = useState<number>(0);
  const [entries, setEntries] = useState<number>(25);
  const [orgPage, setOrgPage] = useState<number>(0);
  const [orgEntries, setOrgEntries] = useState<number>(25);
  const [lastPage, setLastPage] = useState<number>(0);
  const [orgLastPage, setOrgLastPage] = useState<number>(0);
  const { results, orgResults } = useSelector(
    ({
      userAdmin: {
        users: { results },
      },
      organisationAdmin: {
        organisations: { orgResults },
      },
    }: AppState) => ({
      results,
      orgResults,
    }),
  );

  useEffect(() => {
    (() => {
      fetchSearchResults();
      handlePagination(page, entries);
    })();
  }, [page, entries]);

  useEffect(() => {
    (() => {
      fetchSearchResults();
      handlePagination(orgPage, orgEntries);
    })();
  }, [orgEntries, orgPage]);

  const handlePagination = (page: number, entries: number) => {
    const search = createQueryParamsString({ page, entries });
    history.push({
      pathname: history.location.pathname,
      search,
    });
  };

  const fetchSearchResults = async () => {
    try {
      dispatch(showLoader());
      const pagination = { page, entries };
      const orgPagination = { page: orgPage, entries: orgEntries };

      const { username, org } = values;
      let searchTerms = {} as UserSearchTerms;
      let orgSearchTerms = {} as OrganisationSearchTerm;

      // BE is case sensitive
      if (username?.length) searchTerms.username = username.toLowerCase();
      if (org?.length) orgSearchTerms.orgname = org.toLowerCase();

      searchTerms = Object.fromEntries(
        Object.entries(searchTerms).map(([key, value]) => [key, value.trim()]),
      );

      orgSearchTerms = Object.fromEntries(
        Object.entries(orgSearchTerms).map(([key, value]) => [key, value.trim()]),
      );

      if (Object.values(searchTerms).length) {
        const { data, meta } = await API.getSearchedUsers(searchTerms, pagination);

        if (meta.totalItems) {
          if (meta.totalItems <= entries) {
            setLastPage(1);
          } else {
            const lastPageIndex = Math.ceil(meta.totalItems / entries);
            setLastPage(lastPageIndex);
          }
        }

        dispatch(setUserSearchResults(data));
      }

      if (org?.length > 2) {
        const orgData: OrganisationSearchedData = await API.getSearchedOrganisations(
          orgSearchTerms,
          orgPagination,
        );
        dispatch(setOrganisationSearchResults(orgData));
        if (orgData?.meta?.totalItems) {
          if (orgData?.meta?.totalItems <= orgEntries) {
            setOrgLastPage(1);
          } else {
            const lastPageIndex = Math.ceil(orgData?.meta?.totalItems / orgEntries);
            setOrgLastPage(lastPageIndex);
          }
        }
      }
    } catch (error: any) {
      dispatch(setUserSearchResults([]));
      const errorData = error?.response?.data;
      const isHandled = handleErrorCodes(errorData?.errorCode);

      if (!isHandled) return;
      alert.open("Error", formatErrorMessage(error));
    } finally {
      dispatch(hideLoader());
    }
  };

  const {
    errors,
    handleBlur,
    handleChange,
    handleSubmit,
    isSubmitting,
    touched,
    values,
    resetForm,
  } = useFormik({
    initialValues: {
      username: "",
      org: "",
      phone: "",
    },
    onSubmit: fetchSearchResults,
  });

  const createQueryParamsString = (params?: any) => {
    const queryStringResults = queryString.parse(history.location.search);

    // create search query url
    return Object.entries({
      page,
      entries,
      ...queryStringResults,
      ...params,
    })
      .filter((query) => {
        // filter out falsy values (except 0)
        return !!query[1] || query[1] === Number(0);
      })
      .reduce((acc, query, index) => {
        const [key, value] = query;

        if (index === 0) acc += `?${key}=${value}`;
        else acc += `&${key}=${value}`;

        return acc;
      }, "");
  };

  const handleOnEntriesChange = (entries: number) => {
    setPage(0);
    setEntries(entries);
    setLastPage(Math.ceil(results.length / entries));
  };

  const handleOrgOnEntriesChange = (entries: number) => {
    setOrgPage(0);
    setOrgEntries(entries);
    orgResults?.meta && setOrgLastPage(Math.ceil(orgResults?.meta?.totalItems / entries));
  };

  const clearResults = () => {
    resetForm();
    dispatch(clearSearchResults());
    dispatch(clearOrgSearchResults());
    setPage(0);
    setLastPage(0);
    setOrgPage(0);
    setOrgLastPage(0);
    setEntries(25);
    setOrgEntries(25);
  };

  useEffect(() => {
    clearResults();
  }, []);

  const onRowClick = (user: UserSearchResponse) => () => {
    dispatch(setSelectedUser(user));
    history.push(`${SCREEN_PATHS.UPDATE_USER}/${user.customerId}`);
  };

  const redirectToViewOrg = (orgId: string) => () => {
    history.push(`${SCREEN_PATHS.ORG_DETAILS}/${orgId}`);
  };

  const renderHeader = () => (
    <TableRow className={classes.tableHead}>
      <TableCell>User Name</TableCell>
      <TableCell>Organisation Name</TableCell>
      <TableCell>Phone Number</TableCell>
      <TableCell>Created Date</TableCell>
      <TableCell>Created Time</TableCell>
      <TableCell>Last Login</TableCell>
    </TableRow>
  );
  const renderRows = (entry: UserSearchResponse) => {
    const {
      username,
      orgName,
      phone,
      meta: { createdAt, lastLoginTime },
    } = entry;

    return (
      <TableRow key={entry.customerId} className={classes.tableRow}>
        <TableCell style={{ cursor: "pointer" }}>
          <Link onClick={onRowClick(entry)}>{username}</Link>
        </TableCell>
        <TableCell style={{ cursor: "pointer" }}>
          <Link onClick={redirectToViewOrg(entry.orgId)}>{orgName}</Link>
        </TableCell>
        <TableCell>{phone}</TableCell>
        <TableCell>
          {createdAt ? formatInTimeZone(createdAt, "UTC", commonFormatDate) : "-"}
        </TableCell>
        <TableCell>{createdAt ? formatInTimeZone(createdAt, "UTC", "HH:mm") : "-"}</TableCell>
        <TableCell>
          {lastLoginTime ? formatInTimeZone(lastLoginTime, "UTC", commonFormatDate) : "-"}
        </TableCell>
      </TableRow>
    );
  };

  const renderEmptyDataRow = () => (
    <TableRow>
      <TableCell colSpan={6} align="center">
        {`No Matching User Located.`}
      </TableCell>
    </TableRow>
  );

  return (
    <Page title="Update User">
      <form onSubmit={handleSubmit}>
        <Box my={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card elevation={12}>
                <CardHeader title="Search" />
                <Divider />
                <Box p={2}>
                  <Grid container spacing={3}>
                    <Grid item lg={6} xs={12}>
                      <CommonTextField
                        disabled={isSubmitting}
                        error={Boolean(touched.username && errors.username)}
                        fullWidth
                        helperText={touched.username && errors.username}
                        label="User Name"
                        margin="normal"
                        name="username"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="username"
                        value={values.username}
                        variant="outlined"
                      />
                    </Grid>
                    <Grid item lg={6} xs={12}>
                      <CommonTextField
                        disabled={isSubmitting}
                        error={Boolean(touched.org && errors.org)}
                        fullWidth
                        helperText={touched.org && errors.org}
                        label="Organisation Name"
                        margin="normal"
                        name="org"
                        onBlur={handleBlur}
                        onChange={handleChange}
                        type="org"
                        value={values.org}
                        variant="outlined"
                      />
                    </Grid>
                  </Grid>
                </Box>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Box p={2}>
                  <Grid container item>
                    <Grid item lg={4} xs={false} />
                    <Grid item lg={4} xs={12}>
                      <Button
                        color="primary"
                        disabled={
                          (!values.username && !values.org && !values.phone) || isSubmitting
                        }
                        fullWidth
                        type="submit"
                        variant="contained">
                        Search
                      </Button>
                    </Grid>
                    <Grid item lg={4} xs={12}>
                      <Box display="flex" alignItems="flex-end" justifyContent="flex-end">
                        <Button
                          type="reset"
                          disabled={isSubmitting}
                          onClick={clearResults}
                          color="primary"
                          endIcon={<ArrowRightIcon />}
                          variant="text">
                          Clear Results
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </form>
      <Box my={4}>
        <ScreenHeader sx={{ textAlign: "center" }} title="Search Results" />
        <Grid
          style={{
            display: "flex",
            justifyContent: "space-between",
            width: "100%",
            alignItems: "flex-start",
            gap: "20px",
          }}>
          <Grid>
            <ScreenHeader sx={{ marginTop: "32px" }} title="User" />
            <Card elevation={12}>
              <Table
                data={results}
                renderHeader={renderHeader}
                renderRows={renderRows}
                renderEmptyDataRow={renderEmptyDataRow}
                page={page}
                entries={entries}
                onPageChange={setPage}
                onEntriesChange={handleOnEntriesChange}
                lastPage={lastPage}
              />
            </Card>
          </Grid>
          <Grid>
            <OrganisationList
              data={orgResults as OrganisationSearchedData}
              entries={orgEntries}
              setEntries={setOrgEntries}
              page={orgPage}
              lastPage={orgLastPage}
              setPage={setOrgPage}
              handleOnEntriesChange={handleOrgOnEntriesChange}
            />
          </Grid>
        </Grid>
      </Box>
    </Page>
  );
};

export default UpdateUserView;
